import groupBy from 'lodash/groupBy'
import getConfig from 'next/config'
import Link from 'next/link'
import { FiArrowRight } from 'react-icons/fi'
import { IoArrowForward } from 'react-icons/io5'
import { worstLandlords } from 'src/common/Constants'
import { getNewNeighborhoods } from 'src/data/Neighborhoods'

import Separator from '@components/Separator'
import NewBuildingCard from '@components/cards/Building/newBuildingCard'
// import { segment } from 'src/lib/Segments'
import Layout from '@components/layouts'
import ContactCardTopRated from '@components/top-rated/ContactCard'
import { Neighborhood } from '@openigloo/common'
import {
  fetchTopRatedBuildings,
  fetchTopRatedContacts,
  fetchTopReviewedBuildings,
  fetchTopReviewedContacts,
} from '@redux/actions'
import { useAppSelector } from '@redux/hooks'
import { wrapper } from '@redux/store'

function NeighborhoodBuildings({ neighborhoods }: { neighborhoods: Neighborhood[] }) {
  const { publicRuntimeConfig } = getConfig()
  const { topRatedBuildings, topReviewedBuildings, topRatedContacts, topReviewedContacts } =
    useAppSelector((state) => state.misc)

  const canonicalURL = `${publicRuntimeConfig.SITE_BASE_URL}/buildings`

  const groupedBoroughs = groupBy(neighborhoods, function (nbr) {
    return nbr?.borough
  })
  const totalNbrs = {}
  Object.keys(groupedBoroughs).forEach((borough) => {
    totalNbrs[borough] = groupedBoroughs[borough].length
    const groupedByLetter = groupBy(groupedBoroughs[borough], function (nbr) {
      return nbr?.name[0]
    })
    // @ts-expect-error groupBy function screws up type
    groupedBoroughs[borough] = groupedByLetter
  })

  return (
    <Layout title={'Buildings in NYC'} canonicalURL={canonicalURL}>
      <div className="px-4 pb-24 md:pb-6 lg:px-0">
        <div className="mx-auto my-6 max-w-screen-lg">
          <h1 className="text-2xl font-semibold text-dark-900">Boroughs in NYC</h1>
          <div className="my-6 grid gap-4 sm:grid-cols-2 sm:gap-6">
            {Object.keys(groupedBoroughs).map((borough) => {
              return (
                borough !== 'null' && (
                  <Link
                    href={`/buildings/borough/${borough.toLowerCase().split(' ').join('-')}`}
                    key={borough}
                  >
                    <a className="rounded-[5px] bg-light-20 px-4 py-3">
                      <div className="flex items-center space-x-2 font-semibold text-dark-900">
                        <h2>{borough}</h2>
                        <FiArrowRight color="#061938" size={24} />
                      </div>
                      <div className="pt-2 text-sm text-dark-500">
                        {totalNbrs[borough]} neighborhoods
                      </div>
                    </a>
                  </Link>
                )
              )
            })}
          </div>
          <Separator />
        </div>
        <div className="mx-auto my-6 max-w-screen-lg">
          <h2 className="text-2xl font-semibold text-dark-900">
            Top Rated Buildings, Owners, and Management Companies in NYC
          </h2>
          <div className="pt-6 font-semibold text-dark-900">Top Rated Buildings in NYC</div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {topRatedBuildings.slice(0, 3).map((x) => {
              return (
                <NewBuildingCard
                  building={x}
                  key={x.id}
                  segmentBase="toprated"
                  showImage={true}
                  showBuildingDetails={false}
                />
              )
            })}
          </div>
          <div className="pt-6 text-center">
            <Link href={'/nyc/top-rated-buildings'}>
              <a className="rounded-full border border-bluegray-300 bg-blue-25 px-4 py-2 text-sm text-dark-900">
                View all
              </a>
            </Link>
          </div>
          <div className="pt-6 font-semibold text-dark-900">
            Top Rated Owners and Management Companies in NYC
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {topRatedContacts.slice(0, 3).map((x, index) => {
              return (
                <ContactCardTopRated data={x} index={index} key={index} segmentBase="toprated" />
              )
            })}
          </div>
          <div className="my-6 pb-2 text-center">
            <Link href={'/nyc/top-rated-contacts'}>
              <a className="rounded-full border border-bluegray-300 bg-blue-25 px-4 py-2 text-sm text-dark-900">
                View all
              </a>
            </Link>
          </div>
          <Separator />
        </div>
        <div className="mx-auto my-6 max-w-screen-lg">
          <h2 className="text-2xl font-semibold text-dark-900">
            Most Reviewed Buildings, Owners, and Management Companies in NYC
          </h2>
          <div className="pt-6 font-semibold text-dark-900">Most Reviewed Buildings in NYC</div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {topReviewedBuildings.slice(0, 3).map((x) => {
              return (
                <NewBuildingCard
                  building={x}
                  key={x.id}
                  segmentBase="mostreviewed"
                  showImage={true}
                  showBuildingDetails={false}
                />
              )
            })}
          </div>
          <div className="pt-6 text-center">
            <Link href={'/nyc/top-reviewed-buildings'}>
              <a className="rounded-full border border-bluegray-300 bg-blue-25 px-4 py-2 text-sm text-dark-900">
                View all
              </a>
            </Link>
          </div>
          <div className="pt-6 font-semibold text-dark-900">
            Most Reviewed Owners and Management Companies in NYC
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {topReviewedContacts.slice(0, 3).map((x, index) => {
              return (
                <ContactCardTopRated
                  data={x}
                  index={index}
                  key={index}
                  segmentBase="mostreviewed"
                />
              )
            })}
          </div>
          <div className="py-6 text-center">
            <Link href={'/nyc/top-reviewed-contacts'}>
              <a className="rounded-full border border-bluegray-300 bg-blue-25 px-4 py-2 text-sm text-dark-900">
                View all
              </a>
            </Link>
          </div>
          <Separator />
        </div>
        <div className="mx-auto my-6 max-w-screen-lg">
          <h2 className="text-2xl font-semibold text-dark-900">100 Worst Landlords In NYC</h2>
          <div className="mt-2 text-sm text-dark-700">
            Sourced from{' '}
            <a
              className="text-bluegray-300 underline"
              href="https://advocate.nyc.gov/"
              target="_blank"
              rel="nofollow noreferrer"
            >
              The Office of Public Advocate
            </a>
          </div>
          {worstLandlords.map((x) => {
            return (
              <div className="mt-4" key={x}>
                <Link href={`/nyc/100-worst-landlords/${x}`}>
                  <div className="flex cursor-pointer items-center space-x-2 rounded-md bg-light-20 px-4 py-2">
                    <div className="font-semibold text-dark-900">
                      The Worst Landlord Watchlist {x}
                    </div>
                    <IoArrowForward size={24} color="#061938" />
                  </div>
                </Link>
              </div>
            )
          })}
        </div>
      </div>
    </Layout>
  )
}

export const getStaticProps = wrapper.getStaticProps((store) => async () => {
  const [neighborhoods] = await Promise.all([
    getNewNeighborhoods('New York'),
    store.dispatch(fetchTopRatedBuildings(3, 'New York')),
    store.dispatch(fetchTopRatedContacts(3, 'New York')),
    store.dispatch(fetchTopReviewedBuildings(3, 'New York')),
    store.dispatch(fetchTopReviewedContacts(3, 'New York')),
  ])

  return {
    props: {
      neighborhoods,
    },
    revalidate: 345600, // Re-generate the page every 4 days
  }
})

export default NeighborhoodBuildings
